import type { Resource } from './resources'

export const RU_RESOURCES: ReadonlyArray<Resource> = [
  {
    language: 'ru',
    type: 'guide',
    slug: 'what-is-context-debt',
    title: 'Что такое контекстный долг?',
    description:
      'Разбираемся, как разрозненный, устаревший и недостающий контекст замедляет команды — и как обнаружить проблему.',
    author: 'Команда Laplace',
    publishedAt: '2026-08-21',
    readingTime: '6 минут',
    coverImage: '/images/context-debt-guide.png',
    coverImageAlt:
      'Laplace объединяет рабочие источники и показывает пробелы контекста на аналитической панели',
    topics: ['Контекстный долг', 'Context Audit', 'Готовность к AI'],
    integrations: ['Jira', 'Confluence', 'GitHub'],
    featured: true,
    published: false,
    seoTitle: 'Что такое контекстный долг? Практический гайд — Laplace',
    seoDescription:
      'Что такое контекстный долг, как разрозненные и устаревшие знания замедляют команды и как найти проблему до внедрения AI-агентов.',
    relatedResources: ['digital-employees', 'choose-ai-workflow'],
    ctaType: 'context-audit',
    keyTakeaways: [
      'Контекстный долг — это издержки из-за разрозненной, устаревшей или недостающей информации внутри бизнес-процесса.',
      'Он влияет и на людей, и на AI-агентов: неполный контекст делает решения медленнее и менее надёжными.',
      'Context Audit превращает проблему в измеримую карту источников, пробелов, владельцев и следующих действий.',
    ],
    sections: [
      {
        id: 'what-context-debt-means',
        title: 'Что означает контекстный долг',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Каждый процесс зависит от контекста: актуального плана, истории клиента, принятых решений, правил и текущего состояния работы. Контекстный долг накапливается, когда эта информация разбросана по системам, устарела или изначально не была зафиксирована.',
          },
          {
            type: 'paragraph',
            text: 'Последствия знакомы многим командам. Сотрудники ищут данные в разных инструментах, повторяют вопросы, восстанавливают ход решений и перепроверяют информацию, прежде чем начать действовать. AI-агент наследует ту же неопределённость, если базовый контекст не сделан явным и управляемым.',
          },
          {
            type: 'callout',
            title: 'Контекстный долг — это операционный долг',
            body: 'Это не просто проблема документации. Она влияет на скорость процессов, качество решений, адаптацию новых сотрудников и надёжность автоматизации.',
          },
        ],
      },
      {
        id: 'how-it-appears',
        title: 'Как он проявляется в ежедневной работе',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Контекстный долг проще всего заметить в точках передачи работы. Ищите моменты, когда человеку приходится останавливаться, задавать вопросы, сверять или копировать информацию перед следующим шагом.',
          },
          {
            type: 'list',
            items: [
              'Статус проекта в Jira противоречит последнему решению из заметок встречи.',
              'Специалист поддержки не видит, почему для клиента согласовали исключение.',
              'Новому сотруднику нужны объяснения нескольких коллег, чтобы понять, как процесс работает на самом деле.',
              'AI-ассистент даёт правдоподобный ответ, но не может указать источник и подтвердить его актуальность.',
            ],
          },
        ],
      },
      {
        id: 'why-agents-expose-it',
        title: 'Почему AI-агенты выявляют скрытые пробелы',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Человек может компенсировать недостающий контекст опытом и неформальными связями. Агенту нужны более точные инструкции, надёжные источники, определённые инструменты и границы согласования. Поэтому запуск агента становится полезной проверкой самого процесса.',
          },
        ],
      },
      {
        id: 'instructions-and-boundaries',
        title: 'Одних инструкций недостаточно',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'Сильный промпт не восстановит отсутствующий журнал решений и не исправит устаревший регламент. Управляемые агенты работают лучше, когда инструкции, источники контекста, права и согласования с человеком проектируются вместе.',
          },
          {
            type: 'figure',
            src: '/images/context-debt-instructions.png',
            alt: 'Управляемый AI-агент соединяет инструкции, документы, процессы и роли с подтверждением человека',
            caption:
              'Надёжный результат появляется, когда инструкции работают вместе с контекстом, инструментами, правами и человеческим контролем.',
          },
        ],
      },
      {
        id: 'start-with-an-audit',
        title: 'Начните с Context Audit',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Выберите один значимый процесс и составьте карту систем, документов, обсуждений, владельцев и согласований, от которых он зависит. Затем оцените, какой контекст разрознен, устарел, отсутствует или недоступен людям и агентам, которым он нужен.',
          },
          {
            type: 'list',
            items: [
              'Определите процесс и решение, которое он должен поддерживать.',
              'Найдите авторитетный источник для каждого критически важного элемента контекста.',
              'Измерьте время на поиск, проверку и восстановление информации.',
              'Устраните наиболее значимые пробелы до расширения автоматизации.',
            ],
          },
          {
            type: 'callout',
            title: 'Сузьте первый охват',
            body: 'Сфокусированный аудит даёт конкретный план улучшений. Инвентаризация всей компании часто становится ещё одним источником устаревшего контекста.',
          },
        ],
      },
    ],
  },
  {
    language: 'ru',
    type: 'guide',
    slug: 'digital-employees',
    title:
      'Цифровые сотрудники: что это такое и какие задачи им можно поручить',
    description:
      'Разбираемся, чем цифровой сотрудник отличается от чат-бота и обычной автоматизации и как встроить AI-агента в реальный бизнес-процесс.',
    author: 'Команда Laplace',
    publishedAt: '2026-08-21',
    readingTime: '14 минут',
    coverImage: '/images/digital-employees-guide.png',
    coverImageAlt:
      'Цифровой сотрудник объединяет документы, код, календарь, почту и аналитику в едином рабочем процессе',
    topics: [
      'Цифровые сотрудники',
      'AI-агенты для бизнеса',
      'Автоматизация',
      'Digital team',
    ],
    integrations: [
      'Jira',
      'Confluence',
      'GitHub',
      'Документы',
      'Почта',
      'Календари',
    ],
    published: true,
    seoTitle: 'Цифровые сотрудники: задачи и внедрение в бизнесе',
    seoDescription:
      'Что такое цифровой сотрудник, чем AI-агент отличается от чат-бота и как встроить цифровую команду в бизнес-процессы с контролем человека.',
    relatedResources: ['what-is-context-debt', 'choose-ai-workflow'],
    ctaType: 'find-customers',
    keyTakeaways: [
      'Цифровой сотрудник — не автономная замена человека, а AI-агент с конкретной ролью, инструкциями, инструментами и границами ответственности.',
      'Агент полезен там, где нужно регулярно собирать, проверять и сопоставлять данные из нескольких рабочих систем.',
      'Начинать внедрение лучше с одного ограниченного процесса, проверяемого результата и заранее определённых точек человеческого контроля.',
    ],
    sections: [
      {
        id: 'why-separate-ai-chats-are-not-enough',
        title: 'Почему компаниям недостаточно отдельных чатов с AI',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Рабочий контекст редко находится в одном месте. Задачи ведутся в Jira, документация — в Confluence, код и pull request — в GitHub, встречи — в календарях. Дополнительные материалы хранятся в файлах, а часть важных решений остаётся в переписке или памяти отдельных сотрудников.',
          },
          {
            type: 'paragraph',
            text: 'Чтобы понять состояние проекта, человеку приходится переходить между системами, искать связанные документы, проверять статусы задач и сопоставлять изменения в коде с первоначальными требованиями. Руководители регулярно запрашивают сводки, а команды заново собирают контекст перед планированием, релизом или передачей работы.',
          },
          {
            type: 'paragraph',
            text: 'Доступ к нейросети сам по себе эту проблему не решает. Модель может хорошо анализировать предоставленный текст, но она не знает, что сейчас происходит в проектах компании. Если сотрудник вручную копирует в чат задачи, документы и комментарии, AI остаётся отдельным инструментом, а не частью рабочего процесса.',
          },
          {
            type: 'paragraph',
            text: 'Цифровые сотрудники предлагают другой подход: AI-агент получает определённую роль, работает с разрешёнными корпоративными источниками и готовит конкретный результат — например, операционную сводку, контекст по фиче или черновик release notes.',
          },
        ],
      },
      {
        id: 'what-is-a-digital-employee',
        title: 'Что такое цифровой сотрудник',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Цифровой сотрудник — это настроенный AI-агент для бизнеса, встроенный в конкретный процесс компании. Он не является автономной заменой человека или виртуальной копией определённой профессии.',
          },
          {
            type: 'paragraph',
            text: 'Корректнее рассматривать его как программного участника процесса, которому заданы:',
          },
          {
            type: 'list',
            items: [
              'Роль. Агент должен понимать, за какую часть работы он отвечает: собирает проектный контекст, проверяет качество задач или готовит сводку для руководителя.',
              'Ожидаемый результат. Важно определить не абстрактную цель «помогать команде», а проверяемый выход: отчёт о блокировках, список пробелов в документации или черновик обновления.',
              'Инструкции. Они описывают порядок работы, критерии качества, формат ответа и случаи, когда агент должен обратиться к человеку.',
              'Рабочий контекст. Агент использует только доступные ему задачи, документы, репозитории и другие источники.',
              'Инструменты. От набора инструментов зависит, сможет ли агент искать задачи, читать страницы, анализировать изменения или выполнять разрешённые действия.',
              'Права и ограничения. Доступ должен соответствовать рабочему пространству, подключённым системам и полномочиям пользователя. Возможность прочитать данные не означает право их изменить.',
              'Расписание. Повторяемый процесс можно запускать автоматически, например каждое утро или перед еженедельной встречей.',
              'Контроль человека. Человек проверяет выводы, подтверждает значимые действия и сохраняет ответственность за решения.',
              'История выполнения. Результаты регулярных запусков, ошибки и изменяющие действия должны оставаться доступными для проверки.',
            ],
          },
          {
            type: 'callout',
            title: 'Не имитация профессии, а ограниченная роль в процессе',
            body: 'ИИ-сотрудник полезен не тем, что копирует человека, а тем, что стабильно выполняет определённую часть работы в заданных границах.',
          },
        ],
      },
      {
        id: 'agent-vs-chatbot-and-automation',
        title: 'Чем AI-агент отличается от чат-бота и обычной автоматизации',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Эти подходы могут использоваться совместно, но решают разные задачи:',
          },
          {
            type: 'list',
            items: [
              'Чат-бот отвечает на отдельный вопрос в рамках диалога и переданного ему контекста. Он подходит для консультаций, объяснений и разовых запросов.',
              'Обычная автоматизация выполняет заранее заданную последовательность действий и условий. Она эффективна в стабильных процессах с однозначными правилами.',
              'AI-агент анализирует доступный контекст, выбирает подходящие инструменты и готовит результат в рамках своей роли. Он полезен в задачах, где требуется поиск, сопоставление и интерпретация данных из нескольких источников.',
            ],
          },
          {
            type: 'paragraph',
            text: 'Чат-бот начинает работу после сообщения пользователя. Он обычно не следит за состоянием рабочего процесса и не знает, какие источники следует проверить, если их содержимое не было передано в диалог.',
          },
          {
            type: 'paragraph',
            text: 'Классическая автоматизация хорошо справляется с правилом вида: «если произошёл X, перенеси данные из системы A в систему B». Но ей сложнее работать с неоднозначным контекстом — например, определить, почему задача заблокирована или какие изменения не отражены в документации.',
          },
          {
            type: 'paragraph',
            text: 'AI-агент способен выбрать инструменты в зависимости от запроса, собрать данные из разрешённых систем, сопоставить их и подготовить вывод. При этом агент не становится полностью автономным: его возможности ограничиваются доступными инструментами, правами, инструкциями и точками человеческого контроля.',
          },
        ],
      },
      {
        id: 'why-model-access-is-not-enough',
        title: 'Почему одного доступа к нейросети недостаточно',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Обычная языковая модель не знает актуального состояния задач, внутренних решений, структуры проектов и распределения ответственности в конкретной компании. У неё нет автоматического доступа к корпоративной документации или разрешения действовать от имени пользователя.',
          },
          {
            type: 'paragraph',
            text: 'Даже если однажды загрузить в модель большой объём данных, рабочий контекст быстро изменится. В Jira появятся новые статусы и комментарии, в GitHub — изменения в коде, в Confluence — обновлённые решения, а в календаре — новые встречи.',
          },
          {
            type: 'paragraph',
            text: 'Поэтому внедрение искусственного интеллекта в бизнес-процессы требует не только модели, но и управляемой связи с рабочими системами. Агент должен получать информацию через подключённые инструменты, учитывать текущее рабочее пространство и не расширять права пользователя.',
          },
          {
            type: 'paragraph',
            text: 'Не менее важно уметь показывать источники, отделять найденные факты от предположений и сообщать о недостатке данных. Если агент не нашёл решение в документации, он должен обозначить пробел, а не уверенно придумывать ответ.',
          },
        ],
      },
      {
        id: 'how-laplace-implements-digital-employees',
        title: 'Как цифровые сотрудники реализованы в Laplace',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'В Laplace участники организации могут создавать настраиваемых агентов. Для агента задаются имя, описание роли, инструкции, иконка, теги и доступный набор инструментов. Ожидаемый результат можно зафиксировать в описании и инструкциях, а для регулярного запуска — в отдельном prompt автоматизации.',
          },
          {
            type: 'paragraph',
            text: 'Агент доступен в рабочих пространствах организации, но инструменты выполняются в контексте текущего workspace. Это позволяет создавать специализированные роли, не предоставляя каждому агенту доступ ко всем возможностям платформы.',
          },
          {
            type: 'list',
            items: [
              'Jira — чтение и разрешённые действия с задачами, комментариями, участниками и спринтами.',
              'Confluence — поиск, чтение, создание и обновление страниц.',
              'GitHub — репозитории, issues, pull request, комментарии, файлы, дерево проекта и diff.',
              'Документы рабочего пространства — PDF, DOCX, PPTX, XLSX, Markdown и TXT с семантическим поиском.',
              'Почта — отправка через Gmail; поиск, чтение, отправка, ответы и черновики через Yandex Mail.',
              'Календари Google, Yandex и Outlook — чтение событий без их изменения.',
            ],
          },
          {
            type: 'paragraph',
            text: 'Для регулярной автоматизации задаются рабочее пространство, агент, prompt, расписание и часовой пояс. Результат фонового запуска сохраняется как обычный чат с метаданными автоматизации. Доступны история запусков, статусы и повтор после ошибки.',
          },
          {
            type: 'callout',
            title: 'Текущие границы автоматизаций',
            body: 'Это не универсальный конструктор любых многошаговых процессов. Текущая модель Laplace ориентирована на запуск одного настроенного агента по расписанию и работу через доступные ему инструменты.',
          },
          {
            type: 'paragraph',
            text: 'Отдельная возможность платформы — Context Audit. Он помогает находить проблемы в рабочем контексте: неполные задачи, пробелы в документации, слабую связь между задачами и изменениями, незавершённые решения и другие виды контекстного долга. При этом предложения по изменениям в Jira остаются черновиками до явного подтверждения человеком.',
          },
        ],
      },
      {
        id: 'digital-employee-examples',
        title: 'Примеры цифровых сотрудников, которых можно настроить',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Следующие роли — не готовые встроенные шаблоны, а примеры AI-агентов для бизнеса, которых можно собрать в Laplace под процессы конкретной компании.',
          },
        ],
      },
      {
        id: 'digital-project-coordinator',
        title: 'Цифровой координатор проекта',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'Руководителю часто приходится вручную собирать состояние проекта перед встречами и регулярно запрашивать обновления у команды.',
          },
          {
            type: 'list',
            items: [
              'Источники: Jira, Confluence, GitHub и активный календарь.',
              'Работа агента: проверить выполненные и просроченные задачи, связанные изменения в коде, зафиксированные решения и предстоящие встречи; выделить блокировки, риски и вопросы без ответа.',
              'Результат: ежедневная или еженедельная сводка с ключевыми изменениями и вопросами, требующими решения.',
              'Контроль: руководитель проверяет выводы и принимает решения по срокам, приоритетам и распределению ответственности.',
            ],
          },
        ],
      },
      {
        id: 'task-quality-assistant',
        title: 'Ассистент по качеству задач',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'В работу могут попадать задачи без понятного описания, критериев приёмки, приоритета, оценки или ответственного.',
          },
          {
            type: 'list',
            items: [
              'Источники: задачи и комментарии Jira, связанные страницы Confluence.',
              'Работа агента: найти неполные задачи, объяснить, каких данных не хватает, и подготовить предложения по уточнению описания или критериев приёмки.',
              'Результат: список проблемных задач и черновики дополнений для владельцев процесса.',
              'Контроль: агент не изменяет Jira самостоятельно; человек проверяет предложения и явно подтверждает необходимые изменения.',
            ],
          },
        ],
      },
      {
        id: 'digital-technical-editor',
        title: 'Цифровой технический редактор',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'Документация постепенно расходится с реализованным поведением продукта, если изменения не фиксируются вместе с разработкой.',
          },
          {
            type: 'list',
            items: [
              'Источники: Jira, Confluence, GitHub и документы рабочего пространства.',
              'Работа агента: сопоставить требования, существующие страницы и изменения в коде; найти устаревшие описания, отсутствующие разделы и незафиксированные решения.',
              'Результат: перечень расхождений и черновики обновлений документации.',
              'Контроль: технический специалист проверяет корректность текста перед публикацией или обновлением страницы.',
            ],
          },
        ],
      },
      {
        id: 'development-assistant',
        title: 'Ассистент разработки',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'Перед началом работы над фичей или ошибкой разработчик тратит время на восстановление связанного контекста.',
          },
          {
            type: 'list',
            items: [
              'Источники: Jira, Confluence, GitHub и загруженные документы рабочего пространства.',
              'Работа агента: собрать требования, историю решений, связанные задачи, pull request, issues и релевантные файлы; отметить противоречия и недостающие сведения.',
              'Результат: единый обзор по фиче, ошибке или техническому решению со ссылками на источники и открытыми вопросами.',
              'Контроль: разработчик проверяет выводы и самостоятельно определяет способ реализации.',
            ],
          },
        ],
      },
      {
        id: 'release-preparation-assistant',
        title: 'Помощник по подготовке релиза',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'Перед выпуском версии данные о задачах, изменениях и открытых вопросах приходится вручную собирать из нескольких систем.',
          },
          {
            type: 'list',
            items: [
              'Источники: Jira, Confluence и GitHub.',
              'Работа агента: собрать связанные задачи и изменения, проверить связь pull request с задачами, выделить незакрытые вопросы и подготовить черновик release notes.',
              'Результат: структурированный пакет информации для release review.',
              'Контроль: агент не принимает решение о готовности версии; выпуск подтверждают ответственные за продукт, разработку и качество.',
            ],
          },
        ],
      },
      {
        id: 'team-lead-assistant',
        title: 'Помощник руководителя команды',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'Значительная часть времени руководителя может уходить на регулярный сбор операционной информации.',
          },
          {
            type: 'list',
            items: [
              'Источники: Jira, Confluence, GitHub и календарь.',
              'Работа агента: по расписанию анализировать динамику задач, блокировки, пробелы в документации, незавершённые решения и события, к которым нужно подготовиться.',
              'Результат: операционная сводка с приоритетными вопросами и ссылками на исходные данные.',
              'Контроль: руководитель определяет, какие вопросы требуют вмешательства, и несёт ответственность за управленческие решения.',
            ],
          },
        ],
      },
      {
        id: 'which-processes-fit',
        title: 'Какие процессы подходят для цифровых сотрудников',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Хороший первый сценарий не обязательно должен быть самым масштабным. Гораздо важнее, чтобы его можно было ограничить, проверить и сравнить с текущим способом работы.',
          },
          {
            type: 'list',
            items: [
              'Задача регулярно повторяется.',
              'Для результата нужно собрать данные из нескольких систем.',
              'Работа выполняется по понятным правилам.',
              'Результат конкретен и поддаётся проверке.',
              'Процесс отнимает заметное время у специалистов.',
              'Результат допускает человеческую проверку.',
              'Сценарий не требует неограниченного доступа или самостоятельного принятия критических решений.',
            ],
          },
          {
            type: 'paragraph',
            text: 'Часто разумнее начинать с подготовки информации, рекомендаций и черновиков. После проверки качества можно постепенно расширять доступные агенту инструменты и автоматизацию рутинных задач.',
          },
        ],
      },
      {
        id: 'how-to-start',
        title: 'Как начать внедрение цифрового сотрудника',
        level: 2,
        blocks: [
          {
            type: 'list',
            items: [
              '1. Выберите один повторяемый процесс. Не начинайте с цели «автоматизировать работу отдела»: возьмите конкретную задачу — например, еженедельную проектную сводку.',
              '2. Зафиксируйте текущее состояние. Опишите участников, ручные этапы, типичные проблемы и приемлемый результат, чтобы получить базу для сравнения.',
              '3. Определите источники данных. Подключайте только те системы и документы, без которых агент не сможет получить результат.',
              '4. Опишите роль и результат агента. Укажите формат ответа, критерии качества и границы ответственности.',
              '5. Ограничьте доступные действия. Если достаточно чтения и подготовки черновика, агенту не нужны права на изменение внешних систем.',
              '6. Установите точки человеческого контроля. Определите, какие результаты достаточно просмотреть, какие нужно подтвердить и какие действия агенту выполнять нельзя.',
              '7. Запустите ограниченный пилот. Используйте один workspace, одну команду или один проект и соберите примеры ошибок и исключений.',
              '8. Оцените время и качество. Сравните число ручных шагов, полноту результата, долю принятых предложений и повторяющиеся ошибки с исходным состоянием.',
              '9. Расширяйте сценарий после проверки. Добавляйте источники, расписания, действия или соседние процессы только после стабильного пилота.',
            ],
          },
          {
            type: 'callout',
            title: 'Масштабируйте доказавшие пользу процессы',
            body: 'Цифровая команда должна расти вокруг проверенных сценариев, а не вокруг количества созданных агентов.',
          },
        ],
      },
      {
        id: 'digital-team-not-chatbots',
        title: 'Цифровая команда — это система ролей, а не коллекция чат-ботов',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Цифровая команда возникает не тогда, когда компания создаёт много отдельных чатов с AI. Она формируется из специализированных агентов, каждый из которых связан с конкретным бизнес-процессом, получает необходимый контекст и работает в понятных границах.',
          },
          {
            type: 'paragraph',
            text: 'Люди сохраняют ответственность за решения, приоритеты и действия с существенными последствиями. Агентам можно передавать повторяемый поиск, сбор, проверку, сопоставление и подготовку информации.',
          },
          {
            type: 'paragraph',
            text: 'Такой подход позволяет перейти от эпизодического использования нейросетей к системному внедрению искусственного интеллекта в бизнес-процессы.',
          },
          {
            type: 'callout',
            title: 'Digital team вокруг реальной работы',
            body: 'Laplace помогает строить digital team вокруг реальных бизнес-процессов компании — с рабочим контекстом, интеграциями, автоматизациями и контролем человека.',
          },
        ],
      },
      {
        id: 'faq',
        title: 'Частые вопросы',
        level: 2,
        blocks: [
          {
            type: 'faq',
            items: [
              {
                question:
                  'Может ли цифровой сотрудник полностью заменить человека?',
                answer:
                  'Нет. Цифровой сотрудник рассчитан на ограниченную роль внутри процесса. Он может собирать данные, сопоставлять источники, готовить рекомендации и выполнять разрешённые действия, но ответственность за значимые решения остаётся у человека.',
              },
              {
                question:
                  'Чем цифровой сотрудник отличается от обычного чат-бота?',
                answer:
                  'Чат-бот обычно отвечает на отдельный вопрос в рамках диалога. Цифровой сотрудник имеет заданную роль, инструкции, инструменты, рабочий контекст и, при необходимости, расписание. Его результат связан с конкретным процессом компании.',
              },
              {
                question:
                  'Какие задачи лучше автоматизировать в первую очередь?',
                answer:
                  'Лучше начинать с регулярных задач, которые требуют сбора информации из нескольких систем и имеют проверяемый результат: проектных сводок, анализа качества задач, подготовки контекста или release notes.',
              },
              {
                question: 'Можно ли контролировать действия AI-сотрудника?',
                answer:
                  'Да. Контроль строится через набор доступных инструментов, права пользователя и workspace, инструкции агента и точки подтверждения человеком. Для чувствительных сценариев агенту можно оставить только чтение и подготовку черновиков.',
              },
              {
                question:
                  'Нужна ли компании специальная AI-команда для внедрения?',
                answer:
                  'Для ограниченного пилота важнее участие владельца процесса и специалистов, которые знают источники данных и критерии качества. Техническая или AI-команда особенно полезна при масштабировании и работе с чувствительными системами.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    language: 'ru',
    type: 'guide',
    slug: 'choose-ai-workflow',
    title:
      'Почему компании автоматизируют не те процессы — и как найти подходящий workflow для AI',
    description:
      'Разбираемся, как восстановить реальный workflow, найти узкие места и выбрать процесс, в котором AI принесёт проверяемую пользу.',
    author: 'Команда Laplace',
    publishedAt: '2026-08-21',
    readingTime: '13 минут',
    coverImage: '/images/choose-ai-workflow-guide.png',
    coverImageAlt:
      'Process Discovery сопоставляет цифровые следы с объяснением участника бизнес-процесса',
    topics: [
      'Process Discovery',
      'AI-автоматизация',
      'Бизнес-процессы',
      'Digital team',
    ],
    integrations: ['Jira', 'Confluence', 'GitHub', 'Календари'],
    published: false,
    seoTitle: 'Как выбрать бизнес-процесс для автоматизации с AI',
    seoDescription:
      'Почему AI-проекты автоматизируют не те задачи, как восстановить реальный workflow и выбрать процесс для управляемого пилота.',
    relatedResources: ['what-is-context-debt', 'digital-employees'],
    ctaType: 'context-audit',
    keyTakeaways: [
      'Первый вопрос AI-проекта — не какого агента создать, а как в действительности выполняется работа и почему она замедляется.',
      'Реальный workflow можно восстановить только сопоставлением цифровых следов, регламентов и объяснений участников процесса.',
      'Laplace разрабатывает Process Discovery Agent как будущий этап между Context Audit, проектом digital team и измеримым пилотом.',
    ],
    sections: [
      {
        id: 'wrong-first-question',
        title: 'Компании начинают автоматизацию не с того вопроса',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Обсуждение внедрения AI часто начинается с выбора технологии. Команда решает, какую модель использовать, какого агента создать, к каким системам его подключить и какие задачи ему передать.',
          },
          {
            type: 'paragraph',
            text: 'Руководители хотят понять, где быстрее получить экономию. Технические специалисты сравнивают платформы и способы интеграции. Участники процесса предлагают автоматизировать действия, которые кажутся им наиболее трудоёмкими. Все эти вопросы важны, но они возникают слишком рано.',
          },
          {
            type: 'paragraph',
            text: 'Прежде чем выбирать AI-агента, нужно ответить на более базовые вопросы:',
          },
          {
            type: 'list',
            items: [
              'Как процесс выполняется сейчас, а не как он описан в регламенте?',
              'Какие люди, системы и документы в нём участвуют?',
              'Где возникают задержки и ручные переходы?',
              'Какая информация нужна для принятия каждого решения?',
              'Какие исключения не отражены в инструкциях?',
              'Где проходит граница ответственности между участниками?',
              'Как выглядит конкретный и проверяемый результат процесса?',
            ],
          },
          {
            type: 'paragraph',
            text: 'Без такой картины компания рискует автоматизировать самую заметную задачу, а не настоящее узкое место. Например, команда может пытаться ускорить написание кода, хотя большая часть задержек возникает до разработки — во время постановки задачи и согласования требований.',
          },
          {
            type: 'callout',
            title: 'Начните с процесса, а не с агента',
            body: 'Первый вопрос AI-проекта должен звучать не «какого агента нам создать?», а «как в действительности выполняется эта работа и почему она замедляется?».',
          },
        ],
      },
      {
        id: 'formal-vs-real-process',
        title: 'Описанный процесс почти всегда отличается от реального',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'В регламенте workflow выглядит последовательно: один участник завершает этап, передаёт результат следующему, а все решения фиксируются в предусмотренной системе. Реальная работа устроена сложнее.',
          },
          {
            type: 'paragraph',
            text: 'Задачи и статусы находятся в Jira. Требования и архитектурные решения хранятся в Confluence. Реализованные изменения видны в GitHub. Часть договорённостей принимается на встречах. Дополнительные материалы остаются в документах, а некоторые исключения существуют только в памяти опытных сотрудников.',
          },
          {
            type: 'paragraph',
            text: 'Формальный процесс показывает, как работа должна выполняться. Цифровые следы показывают отдельные факты: кто изменил задачу, когда появился pull request или какая страница была обновлена. Но ни один из этих источников сам по себе не объясняет весь workflow.',
          },
          {
            type: 'paragraph',
            text: 'Если провести только интервью, участник может забыть редкое исключение, пропустить привычный ручной шаг или описать процесс таким, каким он должен быть. Многие действия становятся настолько привычными, что человек перестаёт воспринимать их как отдельные этапы.',
          },
          {
            type: 'paragraph',
            text: 'Если изучить только данные рабочих систем, будут видны события, но не всегда их причины. По истории задачи можно обнаружить несколько изменений статуса, но нельзя уверенно определить, почему она возвращалась на предыдущий этап. Календарь покажет встречу, но не подтвердит, какое решение было принято и где оно зафиксировано.',
          },
          {
            type: 'callout',
            title: 'Одного источника недостаточно',
            body: 'Реальный процесс нельзя восстановить по одному регламенту, одному интервью или одной системе. Необходимо сопоставить цифровые следы с человеческим объяснением работы.',
          },
        ],
      },
      {
        id: 'automation-without-discovery',
        title: 'Что происходит, если автоматизировать плохо понятый процесс',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Когда компания переходит к реализации слишком рано, проблемы проявляются уже во время пилота:',
          },
          {
            type: 'list',
            items: [
              'Автоматизируется отдельная заметная задача, а не настоящее узкое место процесса.',
              'Агент получает задачу, но не видит связанных решений, ограничений и истории изменений.',
              'Редкие, но важные исключения обнаруживаются только после ошибок во время пилота.',
              'Автоматизация ускоряет этап, который не нужен или не должен выполняться в текущем виде.',
              'Остаётся непонятно, что агент может делать самостоятельно, что требует подтверждения и кто отвечает за результат.',
              'Без исходных метрик результат невозможно сравнить с прежним способом работы.',
            ],
          },
          {
            type: 'paragraph',
            text: 'В результате команда постоянно дополняет инструкции, ограничивает действия агента и вручную компенсирует отсутствующий контекст. Технически пилот может работать, но общее время прохождения процесса почти не меняется.',
          },
          {
            type: 'callout',
            title: 'Автоматизация усиливает устройство процесса',
            body: 'Если автоматизировать плохо понятый процесс, компания получит не новый способ работы, а более быстрое воспроизведение старого хаоса.',
          },
        ],
      },
      {
        id: 'how-process-discovery-should-work',
        title: 'Как должен работать Process Discovery',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Process Discovery — это восстановление фактического workflow по данным рабочих систем и объяснениям его участников. Его задача — не просто нарисовать схему, а подтвердить, где проходит работа, какие решения принимаются и что мешает процессу двигаться дальше.',
          },
          {
            type: 'paragraph',
            text: 'В Laplace отправной точкой для такого анализа может выступать Context Audit. Он уже помогает находить проблемы рабочего контекста по доступным данным Jira, Confluence, GitHub и активного календаря: неполные задачи, пробелы в документации, слабую связь между объектами и незавершённые решения.',
          },
          {
            type: 'paragraph',
            text: 'Однако Context Audit не является готовой картой бизнес-процесса и не заменяет интервью с участниками. Следующий этап должен соединить найденные цифровые следы с человеческим пониманием работы.',
          },
          {
            type: 'list',
            items: [
              '1. Изучить результаты Context Audit и понять, какие источники доступны и где обнаружены контекстные разрывы.',
              '2. Собрать связанные задачи, документы, изменения в коде и события календаря.',
              '3. Отделить подтверждённые факты от гипотез и неизвестных участков.',
              '4. Провести адаптивное интервью с участником процесса.',
              '5. Сопоставить ответы человека с цифровыми следами.',
              '6. Найти задержки, ручные переходы и точки потери контекста.',
              '7. Сформировать карту реального workflow с участниками, источниками, решениями и исключениями.',
              '8. Спроектировать digital team для подтверждённых задач процесса.',
              '9. Подготовить ограниченный пилот с baseline, критериями качества и точками человеческого контроля.',
            ],
          },
        ],
      },
      {
        id: 'adaptive-interview',
        title: 'Почему интервью должно быть адаптивным',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'Участнику процесса не нужен универсальный длинный опросник. Вопросы должны зависеть от уже найденных данных, пробелов и противоречий.',
          },
          {
            type: 'paragraph',
            text: 'Если задача несколько раз меняла статус, нужно выяснить причины возврата. Если встреча состоялась, но решение не появилось в документации, стоит уточнить, где оно было зафиксировано. Если pull request не связан с задачей, важно понять, является ли это ошибкой или нормальной практикой команды.',
          },
          {
            type: 'paragraph',
            text: 'Слова участника не следует автоматически считать полной моделью процесса. Их нужно сравнить с доступными задачами, документами, изменениями и событиями. Расхождение не всегда означает, что человек ошибается: возможно, часть работы проходит за пределами подключённых систем. Это тоже значимый вывод.',
          },
        ],
      },
      {
        id: 'what-laplace-is-developing',
        title: 'Что разрабатывает Laplace',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Laplace работает над Process Discovery Agent — новым этапом между Context Audit и созданием digital team.',
          },
          {
            type: 'paragraph',
            text: 'Мы разрабатываем агента, который будет помогать соединять данные рабочих систем с адаптивным интервью участника процесса. Он будет задавать уточняющие вопросы не по универсальному сценарию, а в зависимости от найденных пробелов, недоступных источников и противоречий.',
          },
          {
            type: 'paragraph',
            text: 'Process Discovery Agent планируется использовать для подготовки следующих результатов:',
          },
          {
            type: 'list',
            items: [
              'Подтверждённая данными модель процесса.',
              'Визуальная карта workflow.',
              'Список задержек и ручных переходов.',
              'Перечень неизвестных или недоступных участков.',
              'AI-возможности внутри процесса.',
              'Проект цифровой команды.',
              'План пилота с baseline и критериями успеха.',
              'Оценка достоверности выводов.',
            ],
          },
          {
            type: 'callout',
            title: 'Process Discovery Agent находится в разработке',
            body: 'Эта функция не является уже доступной возможностью Context Audit или текущих агентов Laplace. Мы планируем её как следующий этап пути от исследования процесса к управляемому пилоту.',
          },
        ],
      },
      {
        id: 'feature-development-example',
        title: 'Пример: разработка новой функции',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Представим продуктовую команду, которая хочет ускорить выпуск новых функций. На первом обсуждении участники называют главной проблемой скорость написания кода. Логичным решением кажется ассистент разработки.',
          },
          {
            type: 'paragraph',
            text: 'Но анализ фактического workflow показывает другую картину:',
          },
          {
            type: 'list',
            items: [
              'Задачи приходят в разработку без критериев приёмки.',
              'Решения, принятые на встречах, не всегда попадают в Confluence.',
              'Разработчики повторно уточняют требования у менеджера продукта.',
              'Pull request не во всех случаях связан с исходной задачей.',
              'Документация обновляется после релиза или остаётся без изменений.',
            ],
          },
          {
            type: 'paragraph',
            text: 'В предполагаемом сценарии Process Discovery Agent будет сопоставлять задачи Jira, страницы Confluence, изменения GitHub и календарные события. Затем он будет уточнять причины возврата задач, место фиксации решений, правила связи кода с задачами и ответственность за документацию.',
          },
          {
            type: 'paragraph',
            text: 'После сопоставления данных и ответов может выясниться, что основная задержка находится не в написании кода. Время теряется при передаче контекста между постановкой задачи, реализацией и подготовкой выпуска.',
          },
        ],
      },
      {
        id: 'digital-team-project',
        title: 'Как может выглядеть проект digital team',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'Для такого процесса проект цифровой команды может включать:',
          },
          {
            type: 'list',
            items: [
              'Агента, проверяющего качество постановки задач.',
              'Агента, собирающего единый контекст по новой функции.',
              'Цифрового технического редактора.',
              'Помощника по подготовке релиза.',
            ],
          },
          {
            type: 'paragraph',
            text: 'Ассистент написания кода тоже может быть полезен, но он перестаёт считаться единственным или первым решением.',
          },
          {
            type: 'paragraph',
            text: 'Пилот запускается на одной команде и одном типе workflow. До его начала фиксируются время прохождения задачи, число повторных уточнений, возвратов и отсутствующих связей между задачами, документацией и изменениями. Так компания проверяет не общую способность AI генерировать текст, а влияние digital team на конкретный процесс.',
          },
        ],
      },
      {
        id: 'unknowns-and-confidence',
        title:
          'Почему Process Discovery должен показывать unknowns и confidence',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Языковая модель способна построить правдоподобное объяснение даже на основании неполных данных. Для исследования бизнес-процесса это опасное свойство.',
          },
          {
            type: 'paragraph',
            text: 'Если часть workflow не видна в подключённых системах, агент не должен автоматически дорисовывать недостающие этапы. Для каждого значимого вывода важно показывать:',
          },
          {
            type: 'list',
            items: [
              'На каких данных основан вывод.',
              'Что сообщил участник процесса.',
              'Какие источники были недоступны.',
              'Где данные противоречат объяснению.',
              'Насколько достоверен вывод.',
              'Какую информацию необходимо проверить вручную.',
            ],
          },
          {
            type: 'paragraph',
            text: 'Например, агент может подтвердить, что задача трижды возвращалась в работу, но не обнаружить причины. После интервью появится объяснение участника, однако оно останется неподтверждённым, если решение не зафиксировано ни в одном источнике. Возможно, проблема процесса как раз заключается в том, что причины решений нигде не сохраняются.',
          },
          {
            type: 'paragraph',
            text: 'Оценка confidence не должна превращаться в непрозрачное универсальное число. Команде важнее понимать состав доказательств: какие факты подтверждены, какие выводы сделаны на основании интервью и где остаются неизвестные участки.',
          },
          {
            type: 'callout',
            title: 'Границы знания — часть результата',
            body: 'Хороший Process Discovery не только описывает процесс, но и честно показывает границы своего знания.',
          },
        ],
      },
      {
        id: 'how-to-choose-first-workflow',
        title: 'Как выбрать первый workflow для AI',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Подходящий первый процесс должен быть достаточно значимым, чтобы результат имел ценность, и достаточно ограниченным, чтобы команда могла провести управляемый пилот.',
          },
          {
            type: 'list',
            items: [
              'Процесс регулярно повторяется.',
              'У него есть конкретное начало и завершение.',
              'Он проходит через несколько систем или участников.',
              'В нём есть заметные ручные переходы.',
              'Результат можно проверить.',
              'Значимые действия допускают человеческое подтверждение.',
              'Исходное состояние можно зафиксировать.',
              'У процесса есть владелец, заинтересованный в улучшении.',
            ],
          },
          {
            type: 'paragraph',
            text: 'Необязательно выбирать процесс с наибольшими затратами. Для первого пилота полезнее workflow, в котором можно быстро проверить гипотезу, обнаружить исключения и сравнить результат с baseline.',
          },
        ],
      },
      {
        id: 'understand-before-automating',
        title: 'Сначала понять работу — затем строить digital team',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Первый шаг к digital team — не создание агента. Сначала нужно понять, где проходит реальная работа, почему она замедляется и какую её часть действительно стоит передать AI.',
          },
          {
            type: 'paragraph',
            text: 'Регламент даёт только предполагаемую схему. Данные рабочих систем показывают отдельные факты. Интервью объясняет причины и исключения. Только их совместный анализ позволяет восстановить процесс и выбрать подходящий сценарий автоматизации.',
          },
          {
            type: 'callout',
            title: 'От аудита к измеримому пилоту',
            body: 'Laplace развивает путь от Context Audit к Process Discovery, проекту digital team и измеримому пилоту. Если вы хотите проверить этот подход на одном из процессов своей команды, оставьте заявку на демонстрацию.',
          },
        ],
      },
      {
        id: 'faq-process-discovery',
        title: 'Частые вопросы',
        level: 2,
        blocks: [
          {
            type: 'faq',
            items: [
              {
                question: 'Что такое Process Discovery?',
                answer:
                  'Process Discovery — это восстановление реального бизнес-процесса по данным рабочих систем и объяснениям его участников. Результатом должна стать подтверждённая карта этапов, решений, участников, задержек и неизвестных участков.',
              },
              {
                question: 'Чем Process Discovery отличается от Context Audit?',
                answer:
                  'Context Audit помогает находить проблемы рабочего контекста в доступных источниках. Process Discovery должен идти дальше: сопоставлять цифровые следы с адаптивным интервью и формировать модель конкретного workflow. Process Discovery Agent в Laplace пока находится в разработке.',
              },
              {
                question:
                  'Почему недостаточно провести интервью с сотрудниками?',
                answer:
                  'Участник может забыть редкий шаг, не упомянуть привычное ручное действие или описать процесс в соответствии с регламентом. Поэтому ответы нужно сопоставлять с задачами, документами, изменениями и другими цифровыми следами.',
              },
              {
                question:
                  'Как понять, что процесс подходит для AI-автоматизации?',
                answer:
                  'Процесс должен регулярно повторяться, иметь проверяемый результат, понятного владельца и допускать человеческий контроль. Желательно, чтобы он содержал заметный объём поиска, сопоставления данных или ручной передачи контекста.',
              },
              {
                question: 'Нужно ли сначала описывать процессы всей компании?',
                answer:
                  'Нет. Для начала лучше выбрать один workflow, одну команду или один тип задач. Ограниченный пилот позволяет проверить гипотезу и собрать данные без дорогостоящего исследования всей организации.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    language: 'ru',
    type: 'research',
    slug: 'uber-ai-agents-software-factory',
    title:
      'Uber показал, как будет выглядеть компания с цифровыми сотрудниками',
    description:
      'Разбираем Software Factory Uber и выясняем, почему корпоративный AI развивается от отдельных чатов к управляемой цифровой команде.',
    author: 'Команда Laplace',
    publishedAt: '2026-09-07',
    readingTime: '10 минут',
    coverImage: '/images/uber-ai-agents-software-factory.png',
    coverImageAlt:
      'Управляемая цифровая команда из специализированных AI-агентов работает в общем процессе под контролем человека',
    topics: [
      'AI-агенты для бизнеса',
      'Цифровые сотрудники',
      'Автоматизация бизнес-процессов',
      'Корпоративный AI',
      'Uber Software Factory',
      'Knowledge Graph',
    ],
    integrations: ['Jira', 'Confluence', 'GitHub'],
    published: true,
    seoTitle: 'AI-агенты для бизнеса: опыт Uber и рост запросов в 9,4 раза',
    seoDescription:
      'Как Uber строит Software Factory из управляемых AI-агентов и почему бизнесу нужны специализация, корпоративный контекст и измеримый результат.',
    relatedResources: [
      'digital-employees',
      'what-is-context-debt',
      'choose-ai-workflow',
      'ai-native-sdlc-playbook',
      'programmers-manage-ai-coding-agents',
    ],
    ctaType: 'find-customers',
    keyTakeaways: [
      'Опыт Uber показывает переход от отдельных AI-инструментов к управляемым агентам, которые выполняют конкретные процессы разработки с контролем человека.',
      'Ценность агента нужно измерять результатом процесса и его качеством, а не количеством запросов, токенов или созданных агентов.',
      'Laplace развивает тот же принцип для бизнес-процессов: цифровому сотруднику нужны роль, рабочий контекст, инструменты, ограничения и проверяемый результат.',
    ],
    sections: [
      {
        id: 'what-uber-published',
        title: 'Что именно опубликовал Uber',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'В августе 2026 года Uber рассказал, как управляет масштабным использованием AI в разработке. Более 70% pull request в компании уже связываются с работой локальных или облачных агентов, сотрудники создали свыше 3 600 агентных навыков, а число их запусков превысило 30 000 в день.',
          },
          {
            type: 'paragraph',
            text: 'Однако важнее самих масштабов архитектурный вывод Uber. Компания движется от интерактивных сессий, которые начинает инженер, к управляемым агентам. Такие агенты проверяют код, помогают исправлять сбои CI, проводят визуальную проверку изменений, разбирают алерты и выполняют задачи по сопровождению кода. Человек сохраняет контроль через review и эскалации.',
          },
          {
            type: 'metrics',
            items: [
              {
                value: '7×',
                label: 'рост активных пользователей',
                detail: 'с февраля по август 2026 года',
              },
              {
                value: '9,4×',
                label: 'рост агентных запросов',
                detail: 'с февраля по август 2026 года',
              },
              {
                value: '−34%',
                label: 'стоимость 1 000 запросов',
                detail: 'от пикового значения при фиксированной модели',
              },
              {
                value: '−52%',
                label: 'стоимость одной сессии',
                detail: 'от июньского пика при фиксированной модели',
              },
            ],
            sourceNote:
              'По данным Uber Engineering. Конкретные результаты зависят от среды и сценария использования.',
          },
          {
            type: 'source',
            label: 'Первоисточник',
            description:
              'Uber Engineering, “Running a Software Factory Efficiently at Uber Scale”, 27 августа 2026 года.',
            href: 'https://www.uber.com/us/en/blog/efficient-software-factory/',
          },
          {
            type: 'callout',
            title: 'Что мы называем подтверждением идеи Laplace',
            body: 'Uber не упоминает Laplace и не является партнёром проекта. Мы говорим о независимом подтверждении общего принципа: корпоративный AI становится полезнее, когда превращается из отдельного чата в управляемого участника конкретного процесса.',
          },
        ],
      },
      {
        id: 'from-chat-to-managed-agent',
        title: 'От AI-чата к управляемому цифровому сотруднику',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Обычный AI-чат реагирует на отдельный запрос. Пользователь сам собирает материалы, формулирует задачу, переносит результат в рабочую систему и повторяет всё заново при следующем запуске. Такой инструмент ускоряет человека, но редко меняет процесс целиком.',
          },
          {
            type: 'paragraph',
            text: 'Управляемый агент работает иначе. У него есть ограниченная роль, понятный ожидаемый результат, доступные инструменты, рабочий контекст и правила обращения к человеку. Он может запускаться по событию или расписанию и выполнять один и тот же процесс последовательно.',
          },
          {
            type: 'list',
            items: [
              'Роль отвечает на вопрос, за какую часть процесса отвечает агент.',
              'Инструменты определяют, какие источники агент может читать и какие действия выполнять.',
              'Контекст связывает запрос с текущими задачами, документами, изменениями и решениями.',
              'Ограничения задают права доступа и ситуации, в которых требуется подтверждение человека.',
              'Метрика результата показывает, приносит ли агент пользу процессу.',
            ],
          },
          {
            type: 'paragraph',
            text: 'В Laplace мы называем такого участника процесса цифровым сотрудником. Это не попытка скопировать профессию или исключить человека из работы. Это специализированный AI-агент, которому можно передать ограниченную повторяемую часть процесса: собрать статус проекта, проверить качество задач, подготовить контекст по клиентскому запросу или найти расхождения между требованиями и реализацией.',
          },
        ],
      },
      {
        id: 'measure-value-not-activity',
        title: 'Измерять нужно ценность, а не активность AI',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Uber раскладывает затраты на AI-сессию по пользователям, сессиям, шагам, запросам, токенам и цене токена. Но для управляемых агентов компания использует более важный уровень метрик: стоимость завершённого результата вместе с показателем качества.',
          },
          {
            type: 'list',
            items: [
              'Для проверки кода — стоимость одной проверки, точность, полнота и уровень шума.',
              'Для работы с инцидентами — стоимость обработанного алерта и время восстановления.',
              'Для изменений в коде — стоимость принятого pull request и доля откатов.',
            ],
          },
          {
            type: 'paragraph',
            text: 'Этот принцип переносится на любые бизнес-процессы. Для цифрового координатора полезнее считать время подготовки достоверной сводки и число найденных блокировок, чем количество сообщений. Для агента поддержки — время до готового ответа, полноту источников и долю правок специалиста. Для помощника по документации — число подтверждённых расхождений и принятых обновлений.',
          },
          {
            type: 'callout',
            title: 'Количество агентов не является бизнес-метрикой',
            body: 'Цифровая команда создаёт ценность только тогда, когда каждый агент улучшает конкретный результат: сокращает время, повышает полноту, снижает число ошибок или освобождает специалистов от повторяемой работы.',
          },
        ],
      },
      {
        id: 'context-determines-quality',
        title: 'Качество AI-агента определяется качеством контекста',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'В больших организациях агент тратит значительную часть времени не на создание результата, а на поиск правильной информации. Задача может находиться в Jira, решение — в Confluence, реализация — в GitHub, а важное исключение — в истории обсуждений. Если связь между ними неизвестна, AI начинает перебирать источники, расходует больше токенов и всё равно рискует ошибиться.',
          },
          {
            type: 'paragraph',
            text: 'Uber иллюстрирует проблему сравнением одной задачи. Агент с доступом к внутреннему AI Context Graph нашёл нужный набор данных и правильно ответил за 38 секунд. Агент без графа потратил более 20 минут, столкнулся с ошибками и пришёл к неверному выводу. Это отдельный эксперимент Uber, а не универсальная оценка ускорения, но он хорошо показывает значение подготовленного корпоративного контекста.',
          },
          {
            type: 'paragraph',
            text: 'В действующем Laplace цифровые сотрудники получают контекст через подключённые рабочие системы и документы в рамках прав конкретного workspace. Единый Knowledge Graph, который связывает задачи, документы, изменения, решения и участников, находится в бэклоге продукта. Мы рассматриваем его как возможный следующий слой для более согласованной работы цифровой команды, но эта технология пока не является доступной функцией Laplace.',
          },
          {
            type: 'paragraph',
            text: 'Уже сейчас перед автоматизацией важно проверить качество исходного контекста. Если задачи не связаны с требованиями, решения нигде не зафиксированы, а документы устарели, агент унаследует эти пробелы. Context Audit в Laplace помогает обнаружить такой контекстный долг и выбрать процесс, который действительно готов к внедрению AI.',
          },
        ],
      },
      {
        id: 'specialized-models-and-agents',
        title: 'Один лучший AI для всех задач — слабая стратегия',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Uber выбирает модель под конкретную нагрузку, сравнивая стоимость завершённой задачи, качество и надёжность. Главная модель может планировать и оценивать работу, а более доступные модели — выполнять хорошо определённые подзадачи. Такой подход снижает стоимость без автоматического снижения качества.',
          },
          {
            type: 'paragraph',
            text: 'На уровне бизнеса действует тот же принцип специализации. Агенту для подготовки еженедельного статуса не нужны те же инструкции, инструменты и уровень автономности, что агенту для анализа инцидента или проверки договора. Цифровая команда должна состоять из ролей, спроектированных вокруг процессов, а не из копий одного универсального чат-бота.',
          },
        ],
      },
      {
        id: 'what-uber-results-mean',
        title: 'Что означают результаты Uber для других компаний',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'С февраля по август 2026 года число активных пользователей агентных решений Uber выросло в семь раз, а число запросов — в 9,4 раза. При фиксированной модели стоимость тысячи запросов снизилась примерно на 34% от пикового значения, а стоимость сессии — на 52% от июньского пика. Uber отдельно предупреждает, что конкретные показатели зависят от кодовой базы, размера команды и сценариев использования.',
          },
          {
            type: 'paragraph',
            text: 'Поэтому копировать проценты бессмысленно. Полезно копировать метод: выбрать реальную работу, собрать benchmark из её примеров, оценивать качество и стоимость результата, а затем улучшать маршрутизацию моделей, контекст, инструменты и инструкции.',
          },
          {
            type: 'list',
            items: [
              'Начните с одного повторяемого процесса с понятным владельцем.',
              'Зафиксируйте baseline: время, качество, ошибки и ручные передачи контекста.',
              'Определите доступные источники и устраните критические пробелы в данных.',
              'Ограничьте роль и действия агента, добавьте review или подтверждение человека.',
              'Считайте стоимость и качество завершённого результата.',
              'Расширяйте цифровую команду только после проверки пилота.',
            ],
          },
        ],
      },
      {
        id: 'laplace-digital-team',
        title: 'Как этот подход продолжает Laplace',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Laplace переносит принцип управляемых агентов из отдельных технических сценариев в повседневные процессы компании. Организация может создавать цифровых сотрудников с разными ролями и навыками, подключать к ним разрешённые рабочие инструменты и запускать повторяемые задачи по расписанию.',
          },
          {
            type: 'paragraph',
            text: 'Путь начинается не с массового создания агентов, а с анализа процесса. Нужно понять, где теряется контекст, какие действия повторяются, какой результат можно проверить и где должен участвовать человек. После этого ограниченный сценарий превращается в измеримый пилот, а проверенные роли — в цифровую команду.',
          },
          {
            type: 'callout',
            title: 'Software Factory — частный случай более широкой идеи',
            body: 'Uber показывает эту модель на жизненном цикле разработки. Laplace развивает её для других процессов, где работа зависит от поиска, сопоставления и передачи корпоративного контекста.',
          },
        ],
      },
      {
        id: 'faq-uber-ai-agents',
        title: 'Частые вопросы об AI-агентах и подходе Uber',
        level: 2,
        blocks: [
          {
            type: 'faq',
            items: [
              {
                question: 'Что такое Software Factory в понимании Uber?',
                answer:
                  'Это система AI-инструментов и управляемых агентов для разных этапов разработки: создания и проверки кода, исправления CI, работы с инцидентами и сопровождения. Компания управляет моделями, контекстом, инструментами, стоимостью и качеством результата на уровне общей платформы.',
              },
              {
                question: 'Чем управляемый AI-агент отличается от чат-бота?',
                answer:
                  'Чат-бот обычно отвечает на отдельный запрос пользователя. Управляемый агент имеет определённую роль, инструменты, источники контекста, ограничения и критерий результата; он также может выполнять повторяемый процесс по расписанию с контролем человека.',
              },
              {
                question: 'Какие AI-агенты подходят для бизнеса?',
                answer:
                  'Хороший первый сценарий регулярно повторяется, использует несколько рабочих источников, имеет проверяемый результат и допускает человеческий контроль. Примеры: проектная сводка, проверка качества задач, подготовка ответа клиенту или поиск расхождений в документации.',
              },
              {
                question: 'Есть ли в Laplace Knowledge Graph?',
                answer:
                  'Пока нет. Цифровые сотрудники Laplace работают с подключёнными системами и документами в пределах доступного им workspace. Единый Knowledge Graph находится в бэклоге и рассматривается как будущее направление развития общей контекстной основы цифровой команды.',
              },
              {
                question: 'Как измерять эффективность цифрового сотрудника?',
                answer:
                  'Метрика должна отражать результат конкретного процесса: затраченное время, полноту, точность, долю принятых рекомендаций, число ошибок или стоимость завершённой задачи. Количество сообщений и созданных агентов само по себе не показывает бизнес-ценность.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    language: 'ru',
    type: 'research',
    slug: 'ai-native-sdlc-playbook',
    title:
      'Код научился писать себя. Теперь разработке нужно научиться фиксировать решения',
    description:
      'Код теперь появляется за часы. Разбираемся, почему релизы всё ещё занимают недели, что Anthropic предлагает изменить во всём SDLC и где в новом цикле находится Laplace.',
    author: 'Команда Laplace',
    publishedAt: '2026-09-07',
    readingTime: '12 минут',
    coverImage: '/images/ai-native-sdlc-playbook.png',
    coverImageAlt:
      'Шесть этапов AI-native SDLC образуют непрерывный цикл контекста с контрольными точками человека',
    topics: [
      'AI-native SDLC',
      'Agentic SDLC',
      'AI в разработке',
      'Автоматизация разработки',
      'Цифровые сотрудники',
      'Корпоративный контекст',
    ],
    integrations: ['Jira', 'Confluence', 'GitHub', 'Документы'],
    published: false,
    seoTitle: 'AI-native SDLC: подход Anthropic и решение Laplace',
    seoDescription:
      'Что такое AI-native SDLC, почему генерации кода недостаточно и как Laplace связывает задачи, требования, код и контроль человека.',
    relatedResources: [
      'uber-ai-agents-software-factory',
      'programmers-manage-ai-coding-agents',
      'digital-employees',
      'what-is-context-debt',
      'choose-ai-workflow',
    ],
    ctaType: 'context-audit',
    keyTakeaways: [
      'Код стал дешёвым. Дорогим осталось понимание: что строить, почему, с какими ограничениями и что изменилось после генерации.',
      'Anthropic заменяет линейный конвейер непрерывным циклом, где каждый этап оставляет проверяемую память для следующего.',
      'Laplace нужен не внутри редактора кода, а между людьми, агентами и системами — там, где рабочий контекст обычно распадается.',
    ],
    sections: [
      {
        id: 'code-is-not-the-bottleneck',
        title: 'Релиз, который застрял после готового кода',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Представьте: утром команда отдаёт задачу coding-агенту, а к обеду получает работающий diff с тестами. Кажется, релиз должен случиться завтра. Но затем выясняется, что критерии приёмки неполны, решение расходится со старой спецификацией, security-review будет только в четверг, а никто уже не помнит, почему ограничение появилось два года назад.',
          },
          {
            type: 'paragraph',
            text: 'Код готов. Работа — нет. Именно с этого парадокса начинается опубликованный Anthropic AI-Native SDLC Playbook. Когда Build сокращается с недель до часов, старый процесс не ускоряется целиком. Он просто начинает быстрее упираться в Plan, Test, Deploy и Maintain — этапы, которые всё ещё живут в человеческом темпе.',
          },
          {
            type: 'source',
            label: 'Первоисточник',
            description:
              'Louis Claxton, Anthropic, “The AI-Native SDLC playbook”, 21 августа 2026 года.',
            href: 'https://claude.com/blog/the-ai-native-sdlc-playbook',
          },
          {
            type: 'callout',
            title: 'AI не убрал очередь. Он передвинул её',
            body: 'Более быстрая генерация кода быстрее доставляет работу к требованиям, проверкам и согласованиям, которые не были рассчитаны на агентную скорость.',
          },
        ],
      },
      {
        id: 'what-is-ai-native-sdlc',
        title: 'Anthropic разворачивает конвейер в петлю',
        level: 2,
        blocks: [
          {
            type: 'metrics',
            items: [
              {
                value: '6',
                label: 'этапов SDLC',
                detail: 'Plan, Design, Build, Test, Deploy, Maintain',
              },
              {
                value: '1',
                label: 'непрерывный цикл',
                detail: 'каждый результат запускает следующий шаг',
              },
              {
                value: 'часы',
                label: 'вместо недель',
                detail: 'целевая скорость прохождения цикла',
              },
              {
                value: 'человек',
                label: 'над контуром',
                detail: 'задаёт направление, проверяет и управляет риском',
              },
            ],
            sourceNote:
              'Модель AI-native SDLC в изложении Anthropic. Это целевые принципы, а не гарантированные показатели для каждой команды.',
          },
          {
            type: 'paragraph',
            text: 'Обычный SDLC похож на эстафету. Идея переходит к product manager, требования — к архитектору, дизайн — к разработчику, код — к QA и release-команде. Каждая роль получает документ, тикет или ссылку и пытается восстановить замысел предыдущей.',
          },
          {
            type: 'paragraph',
            text: 'Anthropic предлагает перестать воспринимать Plan, Design, Build, Test, Deploy и Maintain как шесть изолированных комнат. В AI-native SDLC это одна петля: принятый результат этапа запускает следующий, а production-инцидент возвращается в начало уже как новый intent.',
          },
          {
            type: 'paragraph',
            text: 'Человек при этом не исчезает из процесса. Он поднимается над петлёй: задаёт направление, подтверждает намерение, принимает риск и разбирает исключения. Агентам достаётся то, что можно повторить, проверить и передать дальше без очередного совещания.',
          },
          {
            type: 'list',
            items: [
              'Plan: исходная проблема превращается в понятный человеку и агенту intent.',
              'Design: intent становится требованиями и технической спецификацией с учётом правил компании.',
              'Build: агент создаёт план, код, тесты и сопутствующую документацию.',
              'Test: проверки и evals выполняются постоянно, а не только на границе этапа.',
              'Deploy: автоматические слои review проверяют стандартные риски, а человек рассматривает критические решения.',
              'Maintain: агенты отслеживают эксплуатационные сигналы, диагностируют проблему и возвращают её в новый цикл.',
            ],
          },
        ],
      },
      {
        id: 'artifacts-connect-the-loop',
        title: 'У каждого решения должна остаться память',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'В модели Anthropic каждый этап заканчивается сохранённым артефактом, который может прочитать следующий агент и проверить человек: intent, spec, plan, diff с тестами, pull request с результатами review или запись об инциденте. История изменений одновременно становится audit trail: видно, кто сформулировал запрос, что создал агент и кто подтвердил результат.',
          },
          {
            type: 'paragraph',
            text: 'Это важнее выбора формата файла. Компания может хранить intent в Git, Jira или другой системе, если определён источник истины и объекты связаны между собой. Проблема начинается, когда задача существует отдельно от решения, спецификация — отдельно от реализации, а причина изменения остаётся только в переписке.',
          },
          {
            type: 'callout',
            title: 'AI-native SDLC — это непрерывность контекста',
            body: 'Агент на следующем этапе не должен заново угадывать, зачем создаётся изменение, какие ограничения действуют и что уже было подтверждено. Он должен продолжать проверяемую цепочку работы.',
          },
        ],
      },
      {
        id: 'context-generation-context',
        title: 'Мы называем это «Контекст → Генерация → Контекст»',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'За два месяца до публикации playbook Anthropic основатель Laplace Александр Некрашенко описал ту же проблему со стороны инженерной команды. Когда агент генерирует тысячу строк за час, понимание системы больше не возникает автоматически в процессе ручного написания кода. Команда может получить работающий результат, но потерять знание о том, почему система устроена именно так.',
          },
          {
            type: 'paragraph',
            text: 'Предложенная формула нового цикла — «Контекст → Генерация → Контекст». На входе команда собирает задачу, ограничения, архитектуру и правила для агента. На выходе возвращает в рабочие системы причины решений, связи с задачами, описание изменений и новое знание о продукте. Следующая итерация начинается с этого сохранённого контекста, а не с повторного исследования кода.',
          },
          {
            type: 'source',
            label: 'Авторская колонка основателя Laplace',
            description:
              'Александр Некрашенко, «Тысяча строк кода за час. Потеря понимания системы за три дня», vc.ru.',
            href: 'https://vc.ru/ai/2981658-ponimanie-sistemy-v-razrabotke-kod-protiv-konteksta',
          },
          {
            type: 'paragraph',
            text: 'Anthropic формализует этот принцип на уровне всего SDLC. Intent, spec, plan, diff, результаты review и запись об инциденте становятся последовательными носителями контекста. Форматы отличаются, но задача одна: не позволить пониманию исчезнуть между генерацией, проверкой и следующей итерацией.',
          },
          {
            type: 'callout',
            title:
              'Код остаётся результатом, но перестаёт быть единственным носителем знания',
            body: 'В AI-native разработке команде нужна отдельная, управляемая система контекста: что изменилось, зачем, на основании каких требований и кто это подтвердил.',
          },
        ],
      },
      {
        id: 'laplace-context-layer',
        title: 'Laplace работает этажом выше кода',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Coding-агент видит репозиторий. Но причина, по которой ему вообще понадобилось менять код, почти всегда живёт где-то ещё. Клиент пожаловался в одном месте, product manager создал задачу в другом, требования обновили в Confluence, архитектурное исключение обсудили на встрече, а прошлую реализацию можно понять только по GitHub.',
          },
          {
            type: 'paragraph',
            text: 'Laplace строит не ещё один генератор кода, а командный слой над этими разрывами. Внутри workspace цифровые сотрудники получают разрешённые источники, определённую роль и инструменты. Один восстанавливает контекст фичи, другой проверяет полноту постановки, третий готовит release-пакет. Повторяемую работу можно запускать по расписанию, а значимые действия оставлять на подтверждение человеку.',
          },
          {
            type: 'list',
            items: [
              'Jira даёт задачи, статусы, комментарии, участников и историю работы.',
              'Confluence хранит требования, решения, регламенты и проектную документацию.',
              'GitHub связывает требования с issues, pull request, diff и файлами реализации.',
              'Документы workspace дополняют процесс спецификациями, таблицами, презентациями и другими материалами.',
              'Context Audit показывает неполные задачи, пробелы документации, слабые связи и незавершённые решения.',
              'Цифровые сотрудники собирают, проверяют и передают контекст для конкретной роли процесса.',
            ],
          },
          {
            type: 'paragraph',
            text: 'Laplace не заменяет среду программирования, CI/CD или coding-агента. Его роль — сделать корпоративный контекст доступным управляемой цифровой команде и сократить потери информации на переходах между людьми, агентами и системами.',
          },
        ],
      },
      {
        id: 'plan-and-design',
        title: 'Понедельник. Запрос ещё не стал задачей',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Новый цикл начинается не с промпта «напиши фичу». В понедельник нужно поймать intent: чья это проблема, какой результат нужен, какие системы затронуты, где проходят границы и что пока неизвестно. В модели Anthropic этот черновик проверяет product owner, и только после этого он становится основанием для дизайна.',
          },
          {
            type: 'paragraph',
            text: 'В Laplace цифровой аналитик или координатор может собрать связанный контекст из задач, документации, прошлых решений и клиентских материалов, найти противоречия и подготовить черновик постановки. Context Audit помогает увидеть, какой информации не хватает до передачи работы в разработку.',
          },
          {
            type: 'list',
            items: [
              'Найти первоначальный запрос и затронутых пользователей.',
              'Сопоставить задачу с актуальными требованиями и прошлыми решениями.',
              'Выявить отсутствующие критерии приёмки, ограничения и владельцев.',
              'Подготовить структурированный brief для product owner и engineering.',
              'Оставить утверждение требований ответственному человеку.',
            ],
          },
        ],
      },
      {
        id: 'build-and-test',
        title: 'Среда. Код уже готов, понимание ещё нужно проверить',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'К среде спецификация превращается в план, код и тесты. Здесь coding-агент наконец оказывается в своей стихии. Anthropic предлагает держать правила проекта в версионируемых инструкциях, запускать проверки до заявления о готовности и начинать исправление бага с теста, который воспроизводит ошибку.',
          },
          {
            type: 'paragraph',
            text: 'Laplace может подготовить входной контекст для такой сессии: связанные требования, решения, задачи, pull request и релевантные документы. После реализации цифровой сотрудник может сопоставить diff с исходной постановкой, проверить наличие связей и выделить вопросы для review. Фактическое выполнение тестов и изменение кода остаётся задачей coding-агента и инженерного toolchain.',
          },
          {
            type: 'callout',
            title: 'Сильный coding-агент не исправляет слабую постановку',
            body: 'Если входной intent неполон или противоречит документации, агент способен очень быстро реализовать не то изменение. Поэтому качество Plan и Design становится частью качества кода.',
          },
        ],
      },
      {
        id: 'deploy-and-maintain',
        title:
          'Пятница. Релиз заканчивается новой памятью, а не кнопкой Deploy',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'К пятнице вопрос уже не в том, способен ли агент нажать Deploy. Вопрос в том, что можно пропустить автоматически, а где компания обязана остановиться и принять решение. Anthropic предлагает отдать агентам стандартные проверки, а внимание людей сохранить для критических, регулируемых и неоднозначных изменений.',
          },
          {
            type: 'paragraph',
            text: 'В текущем Laplace действия зависят от доступных агенту инструментов и прав workspace. Предложения по изменениям могут оставаться черновиками до явного подтверждения. Цифровой release-координатор может собрать задачи, изменения, документацию и открытые вопросы в единый пакет для решения о выпуске, но само решение остаётся за ответственными людьми.',
          },
          {
            type: 'paragraph',
            text: 'После релиза цифровой сотрудник может регулярно собирать сигналы из подключённых источников, сопоставлять инциденты с изменениями и готовить контекст для следующей задачи. Полностью событийный цикл, в котором production-алерт автоматически создаёт intent и запускает цепочку агентов, пока не является готовой возможностью Laplace.',
          },
        ],
      },
      {
        id: 'current-boundaries',
        title: 'Где красивая схема сталкивается с реальностью',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'На картинке петля замыкается одним движением. В компании между стрелками находятся старые системы, разные владельцы, требования безопасности и накопленный контекстный долг. Поэтому AI-native SDLC — операционная модель, а не функция, которую можно включить одной кнопкой.',
          },
          {
            type: 'list',
            items: [
              'Laplace уже соединяет рабочие источники и позволяет создавать специализированных цифровых сотрудников.',
              'Context Audit уже помогает находить контекстный долг по доступным данным.',
              'Автоматизации Laplace запускают одного настроенного агента по расписанию через доступные ему инструменты.',
              'Произвольная событийная оркестрация всех шести стадий SDLC пока не реализована.',
              'Единый Knowledge Graph и Process Discovery Agent находятся в развитии и не являются доступными функциями продукта.',
            ],
          },
          {
            type: 'paragraph',
            text: 'Поэтому разумный сценарий внедрения — использовать Laplace рядом с coding-агентами: сначала наладить контекст и отдельные роли на самых болезненных handoff, измерить эффект, а затем постепенно связывать проверенные этапы.',
          },
        ],
      },
      {
        id: 'how-to-start',
        title: 'Что можно изменить уже в понедельник',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'В понедельник не нужно объявлять трансформацию всех шести этапов. Достаточно найти одно место, где работа регулярно останавливается ради восстановления контекста, и задать к нему восемь практических вопросов.',
          },
          {
            type: 'list',
            items: [
              '1. Выберите один handoff: идея → постановка, спецификация → разработка, pull request → релиз или инцидент → новая задача.',
              '2. Измерьте baseline: время ожидания, число возвратов, пропущенные требования и ручной поиск информации.',
              '3. Определите источник истины и связи между Jira, Confluence, GitHub и дополнительными документами.',
              '4. Проведите Context Audit и устраните критические пробелы.',
              '5. Создайте одну цифровую роль с ограниченными инструментами и проверяемым результатом.',
              '6. Определите точку человеческого подтверждения и ответственность за решение.',
              '7. Сравните длительность, полноту и качество нового процесса с baseline.',
              '8. Автоматизируйте следующий переход только после устойчивого результата.',
            ],
          },
          {
            type: 'callout',
            title: 'Начните с потери контекста',
            body: 'Самая полезная первая автоматизация часто находится не там, где пишется код, а там, где команда заново выясняет, что нужно сделать, почему и с какими ограничениями.',
          },
        ],
      },
      {
        id: 'faq-ai-native-sdlc',
        title: 'Частые вопросы об AI-native SDLC',
        level: 2,
        blocks: [
          {
            type: 'faq',
            items: [
              {
                question: 'Что такое AI-native SDLC?',
                answer:
                  'Это жизненный цикл разработки, в котором AI участвует на всех этапах — от фиксации намерения и проектирования до тестирования, выпуска и эксплуатации. Этапы связаны проверяемыми артефактами, а люди подтверждают решения, требующие суждения и ответственности.',
              },
              {
                question: 'Чем AI-native SDLC отличается от AI-кодинга?',
                answer:
                  'AI-кодинг ускоряет создание и изменение кода. AI-native SDLC перестраивает весь процесс вокруг этого ускорения: планирование, передачу требований, проверки, governance, выпуск и возврат эксплуатационных знаний в следующий цикл.',
              },
              {
                question: 'Заменяет ли Laplace Claude Code?',
                answer:
                  'Нет. Coding-агент работает непосредственно с кодом и инженерными инструментами. Laplace помогает собирать корпоративный контекст из рабочих систем, выявлять его пробелы и организовывать цифровые роли вокруг процесса.',
              },
              {
                question: 'Может ли Laplace автоматизировать весь SDLC?',
                answer:
                  'Пока нет. Laplace поддерживает специализированных агентов, подключённые рабочие источники, Context Audit и запуски по расписанию. Полностью событийная оркестрация всех шести стадий SDLC не является текущей возможностью продукта.',
              },
              {
                question: 'С какого этапа SDLC лучше начать?',
                answer:
                  'Начните с перехода, где команда регулярно теряет время или контекст и может измерить результат: подготовка постановки, сбор контекста перед разработкой, проверка соответствия реализации требованиям или подготовка release-пакета.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    language: 'ru',
    type: 'research',
    slug: 'programmers-manage-ai-coding-agents',
    title:
      'Разработчики уже не пишут код руками. Они управляют агентами, которые пишут его за них',
    description:
      'В Anthropic Claude создаёт больше 80% вливаемого кода, а OpenAI построила продукт на миллион строк без ручного программирования. Разбираемся, кем становится разработчик в мире coding-агентов.',
    author: 'Команда Laplace',
    publishedAt: '2026-09-07',
    readingTime: '11 минут',
    coverImage: '/images/programmers-manage-ai-coding-agents.png',
    coverImageAlt:
      'Инженер управляет несколькими AI-агентами, которые параллельно проектируют, пишут и проверяют код',
    topics: [
      'AI-агенты для программирования',
      'Claude Code',
      'OpenAI Codex',
      'Kimi Code',
      'Agentic Coding',
      'Будущее программистов',
    ],
    integrations: ['GitHub', 'Jira', 'Confluence'],
    published: false,
    seoTitle: 'AI-агенты пишут код: Claude Code, Codex и Kimi',
    seoDescription:
      'Claude пишет более 80% кода Anthropic, а Codex создал продукт на миллион строк. Как AI-агенты меняют работу программистов и зачем им Laplace.',
    relatedResources: [
      'ai-native-sdlc-playbook',
      'uber-ai-agents-software-factory',
      'what-is-context-debt',
      'digital-employees',
    ],
    ctaType: 'context-audit',
    keyTakeaways: [
      'В передовых AI-командах набор кода уже перестал быть основной работой инженера: человек формулирует намерение, задаёт среду, проверяет результат и принимает решения.',
      'Claude Code, Codex и Kimi Code работают с репозиторием, командами и тестами, поэтому это уже агенты исполнения, а не автодополнение следующей строки.',
      'Когда код производится быстрее, главным ограничением становятся контекст, требования, архитектурные решения и контроль — именно этот слой помогает организовать Laplace.',
    ],
    sections: [
      {
        id: 'five-months-without-code',
        title: 'Пять месяцев без единой строки кода',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'В феврале 2026 года OpenAI описала внутренний эксперимент, который ещё недавно выглядел бы как провокация. Небольшая команда создала работающий продукт примерно на миллион строк. В нём были приложение, тесты, CI, документация, наблюдаемость и внутренние инструменты. Люди не написали вручную ни одной строки — весь репозиторий создавал Codex.',
          },
          {
            type: 'paragraph',
            text: 'Три инженера направляли агента через задачи и обратную связь. За пять месяцев команда влила около 1500 pull request и оценила срок разработки примерно в десятую часть от обычного. Это не лабораторный benchmark: продукт использовали сотни внутренних пользователей и внешние alpha-тестировщики.',
          },
          {
            type: 'source',
            label: 'OpenAI: Harness engineering in an agent-first world',
            href: 'https://openai.com/index/harness-engineering/',
            description:
              'Разбор эксперимента OpenAI с продуктом, весь код которого написал Codex.',
          },
          {
            type: 'callout',
            title: 'Человек ставит направление. Агент исполняет',
            body: 'Инженер не исчез из процесса. Исчезла необходимость лично набирать каждую реализацию: работа переместилась в постановку задачи, устройство среды, обратную связь и оценку результата.',
          },
        ],
      },
      {
        id: 'anthropic-crossed-eighty-percent',
        title: 'Anthropic уже перешла отметку 80%',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'У Anthropic это не отдельный эксперимент. По данным компании, к маю 2026 года Claude был автором более 80% кода, который команда вливала в основную кодовую базу. До исследовательского запуска Claude Code в феврале 2025 года показатель находился на уровне нескольких процентов.',
          },
          {
            type: 'metrics',
            items: [
              {
                value: '>80%',
                label: 'кода Anthropic написал Claude',
                detail: 'Доля влитого кода по состоянию на май 2026 года.',
              },
              {
                value: '8×',
                label: 'больше строк на инженера',
                detail: 'Во II квартале 2026 года относительно 2024 года.',
              },
              {
                value: '+67%',
                label: 'pull request на инженера',
                detail:
                  'Рост, который Anthropic связывает с использованием Claude Code.',
              },
              {
                value: '70–90%',
                label: 'кода с участием Claude Code',
                detail:
                  'Диапазон по инженерным командам Anthropic в январе 2026 года.',
              },
            ],
            sourceNote:
              'Внутренние данные Anthropic. Строки кода и число PR измеряют объём, а не качество; сама компания прямо указывает на это ограничение.',
          },
          {
            type: 'source',
            label: 'Anthropic: When AI builds itself',
            href: 'https://www.anthropic.com/institute/recursive-self-improvement',
            description:
              'Данные Anthropic о доле кода Claude и изменении инженерного throughput.',
          },
          {
            type: 'source',
            label: 'Anthropic: Claude Code contribution metrics',
            href: 'https://claude.com/blog/contribution-metrics',
            description:
              'Методика подсчёта assisted code и pull request, связанных с Claude Code.',
          },
        ],
      },
      {
        id: 'from-autocomplete-to-agent',
        title: 'Это уже не автодополнение',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Copilot первого поколения ждал, пока человек откроет файл и начнёт печатать. Coding-агент получает цель, исследует репозиторий, меняет несколько файлов, запускает команды и тесты, анализирует ошибку и повторяет цикл. Claude Code и Codex сделали этот режим массовым; Kimi Code строит продукт вокруг той же модели поведения.',
          },
          {
            type: 'source',
            label: 'Kimi: Kimi Code introduction',
            href: 'https://www.kimi.ai/resources/kimi-code-introduction',
            description:
              'Официальное описание агента, который анализирует репозиторий, планирует, выполняет команды и самостоятельно повторяет попытки.',
          },
          {
            type: 'list',
            items: [
              'Автодополнение предлагает фрагмент; агент выполняет законченную задачу.',
              'Автодополнение видит место ввода; агент исследует связи по всему репозиторию.',
              'Автодополнение ждёт человека; агент самостоятельно запускает инструменты и реагирует на результат.',
              'Человек редактировал реализацию; теперь он всё чаще редактирует намерение, ограничения и критерии проверки.',
            ],
          },
        ],
      },
      {
        id: 'what-programmer-does-now',
        title: 'Если не писать код, то что теперь делает программист?',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Самая точная аналогия — переход от исполнителя к режиссёру производственной системы. Сильный инженер выбирает проблему, превращает её в проверяемую задачу, даёт агенту доступ к нужным инструментам, задаёт архитектурные границы и строит короткий цикл обратной связи.',
          },
          {
            type: 'list',
            items: [
              'Формулирует намерение и критерии готовности до запуска агента.',
              'Делает репозиторий понятным: фиксирует команды, правила, архитектуру и ограничения.',
              'Разбивает большую цель на части, которые можно независимо проверить.',
              'Проверяет поведение системы, риски и соответствие бизнес-задаче.',
              'Превращает повторяющуюся обратную связь в тесты, правила и автоматические проверки.',
              'Решает, что вообще стоит строить, а не только как это реализовать.',
            ],
          },
          {
            type: 'paragraph',
            text: 'Знание языка и архитектуры остаётся критичным: без него человек не отличит удачное решение от правдоподобной ошибки. Но печать синтаксиса становится всё меньшей частью работы.',
          },
        ],
      },
      {
        id: 'the-new-bottleneck',
        title: 'Код стал дешёвым. Решения — нет',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Когда агент может создать десять реализаций за время одной, дефицитом становится не код. Дефицитом становятся внимание человека и контекст: актуальная постановка, история решений, ограничения безопасности, данные клиента и понимание того, как изменение повлияет на остальные процессы.',
          },
          {
            type: 'callout',
            title: 'Скорость агента умножает качество контекста',
            body: 'Хорошо описанное решение быстро превращается в работающую систему. Устаревшая постановка так же быстро превращается в десятки файлов, тестов и уверенно оформленный неправильный pull request.',
          },
          {
            type: 'paragraph',
            text: 'Поэтому следующий конкурентный разрыв возникнет не между компаниями с агентами и без них. Он возникнет между компаниями, чьи агенты видят целостный рабочий контекст, и компаниями, где они получают случайный набор тикетов, документов и сообщений.',
          },
        ],
      },
      {
        id: 'where-laplace-fits',
        title: 'Зачем coding-агентам Laplace',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Laplace не заменяет Claude Code, Codex или Kimi Code и не конкурирует с ними за место в редакторе. Coding-агент отвечает за исполнение внутри репозитория. Laplace работает с контекстом команды и процесса: помогает подключать рабочие источники, проводить Context Audit, находить пробелы и создавать специализированные цифровые роли с ограниченным набором инструментов.',
          },
          {
            type: 'paragraph',
            text: 'Практическая связка выглядит так: Jira хранит задачу, Confluence — требования и решения, GitHub — реализацию; Laplace помогает агенту или человеку собрать связанный контекст и проверить его полноту, а coding-агент выполняет изменение и возвращает результат в процесс.',
          },
          {
            type: 'callout',
            title: 'Граница текущего продукта',
            body: 'Laplace уже поддерживает подключённые источники, Context Audit, специализированных агентов и запуски по расписанию. Единый Knowledge Graph и полностью событийная оркестрация нескольких агентов находятся в развитии и пока недоступны.',
          },
        ],
      },
      {
        id: 'what-to-measure',
        title: 'Не считайте строки. Считайте результат',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Восемь раз больше строк может означать ускорение, а может означать восемь раз больше кода для сопровождения. Поэтому внедрение coding-агентов стоит оценивать по тому, что важно пользователю и бизнесу.',
          },
          {
            type: 'list',
            items: [
              'Время от принятого решения до работающего изменения.',
              'Доля задач, прошедших проверку без повторной разработки.',
              'Количество дефектов и откатов после релиза.',
              'Время человеческого внимания на одну завершённую задачу.',
              'Полнота требований и решений, сохранённых для следующего цикла.',
            ],
          },
        ],
      },
      {
        id: 'faq-coding-agents',
        title: 'Частые вопросы о coding-агентах',
        level: 2,
        blocks: [
          {
            type: 'faq',
            items: [
              {
                question:
                  'Правда ли, что программисты уже не пишут код руками?',
                answer:
                  'В отдельных передовых командах — да. Anthropic сообщает, что Claude написал более 80% вливаемого кода, а экспериментальная команда OpenAI создала продукт без ручного написания кода. Для всей индустрии такой статистики пока нет.',
              },
              {
                question: 'Чем coding-агент отличается от AI-автодополнения?',
                answer:
                  'Автодополнение предлагает фрагменты рядом с курсором. Coding-агент получает задачу, исследует репозиторий, меняет файлы, запускает команды и тесты, исправляет ошибки и может подготовить pull request.',
              },
              {
                question: 'Заменят ли Claude Code, Codex и Kimi программистов?',
                answer:
                  'Они сокращают объём ручного набора и меняют состав работы. Ответственность за постановку, архитектуру, безопасность, проверку и продуктовые решения остаётся у людей, хотя число задач на одного инженера может заметно вырасти.',
              },
              {
                question:
                  'Как подготовить компанию к работе с coding-агентами?',
                answer:
                  'Начните с одного репозитория и измеримого класса задач. Зафиксируйте правила и команды, подключите тесты, определите человеческие точки контроля и проверьте качество контекста в Jira, документации и коде.',
              },
              {
                question: 'Как Laplace связан с AI-программированием?',
                answer:
                  'Laplace работает на уровне командного контекста и процессов. Он помогает собрать данные из рабочих систем, выявить пробелы и организовать цифровые роли вокруг coding-агента, не заменяя сам инструмент программирования.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    language: 'ru',
    type: 'guide',
    slug: 'corporate-ai-access',
    title: 'Корпоративный доступ к ИИ: как дать нейросеть всей команде',
    description:
      'Почему личные подписки сотрудников на нейросети не масштабируются на команду и как устроен корпоративный доступ к ИИ.',
    author: 'Команда Laplace',
    publishedAt: '2026-09-09',
    readingTime: '8 минут',
    coverImage: '/images/corporate-ai-access.png',
    coverImageAlt:
      'Сотрудники получают управляемый доступ к корпоративному ИИ через единое рабочее пространство с подключёнными источниками данных',
    topics: ['Корпоративный ИИ', 'Доступ к нейросетям', 'Внедрение AI'],
    integrations: ['Jira', 'Confluence', 'GitHub', 'Почта', 'Календари'],
    published: false,
    seoTitle:
      'ChatGPT для компании: как организовать корпоративный доступ к ИИ — Laplace',
    seoDescription:
      'Личные подписки на нейросети не работают в масштабе команды. Разбираем три способа дать сотрудникам корпоративный доступ к ИИ и что проверить перед выбором.',
    relatedResources: ['digital-employees', 'choose-ai-workflow'],
    ctaType: 'build-agent',
    keyTakeaways: [
      'Личная подписка даёт сотруднику доступ к модели, но не к рабочему контексту: задачам, документам, коду и переписке команды.',
      'Корпоративный доступ отличается не ценой за место, а управляемостью: общими правами, единым счётом, ссылками на источники и подтверждением действий.',
      'Начинать имеет смысл с одного процесса и небольшой группы, а не с одновременной раздачи доступа всей компании.',
    ],
    sections: [
      {
        id: 'how-it-starts',
        title: 'Как ИИ обычно появляется в компании',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Почти всегда сценарий один и тот же. Сначала нейросетью начинает пользоваться один человек: аналитик переписывает требования, разработчик разбирает чужой код, менеджер готовит письмо клиенту. Получается быстрее, чем раньше, и об этом узнают коллеги.',
          },
          {
            type: 'paragraph',
            text: 'Через несколько месяцев подписка есть у половины отдела. Каждый оформил её сам, каждый платит своей картой, каждый пользуется по-своему. Формально ИИ в компании уже внедрён. Фактически у компании нет ни общего результата, ни понимания, что происходит.',
          },
          {
            type: 'list',
            items: [
              'Никто не знает, сколько всего компания тратит на нейросети и кто именно ими пользуется.',
              'Удачные способы работы не расходятся по команде: они остаются в личных чатах.',
              'Часть сотрудников копирует во внешний сервис фрагменты задач, договоров и кода.',
              'При увольнении вместе с человеком уходит всё, что он успел настроить.',
            ],
          },
        ],
      },
      {
        id: 'what-personal-subscriptions-miss',
        title: 'Чего не хватает личным подпискам',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Личная подписка решает ровно одну задачу: даёт человеку доступ к модели. Всё остальное сотрудник приносит руками. Он открывает задачу в трекере, копирует описание, добавляет кусок документа, вспоминает договорённости из переписки и только после этого получает ответ.',
          },
          {
            type: 'paragraph',
            text: 'Именно этот ручной перенос и определяет качество результата. Модель отвечает хорошо ровно настолько, насколько полно человек пересказал ей ситуацию. Если сотрудник забыл упомянуть принятое на прошлой неделе решение, ответ будет уверенным и неправильным.',
          },
          {
            type: 'callout',
            title: 'Проблема не в модели, а в контексте',
            body: 'Одна и та же нейросеть даёт разный результат в зависимости от того, что она знает о вашей работе. Личная подписка открывает доступ к модели и не открывает доступ к контексту команды.',
          },
        ],
      },
      {
        id: 'three-options',
        title: 'Три способа дать команде доступ к ИИ',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'На практике компании выбирают между тремя вариантами. Они отличаются не столько стоимостью, сколько тем, что именно получает команда и какие риски остаются на компании.',
          },
        ],
      },
      {
        id: 'option-personal',
        title: 'Личные подписки с компенсацией расходов',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'Самый частый вариант. Сотрудники оформляют подписки сами, компания возмещает затраты. Запускается мгновенно и не требует никаких решений.',
          },
          {
            type: 'paragraph',
            text: 'Работает, пока пользователей единицы. Дальше начинаются издержки: разрозненная оплата, невозможность провести расходы, отсутствие общих правил и полная непрозрачность того, какие данные уходят наружу. Компания оплачивает инструмент, но не владеет результатом его работы.',
          },
        ],
      },
      {
        id: 'option-shared-account',
        title: 'Один общий аккаунт на всех',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'Выглядит как экономия: одна подписка, пароль в общем менеджере, пользуются все. На деле это худший из вариантов.',
          },
          {
            type: 'paragraph',
            text: 'Совместное использование учётной записи нарушает условия большинства сервисов, и аккаунт могут заблокировать вместе со всей историей. Переписка перемешивается, авторство теряется, понять, кто и что запрашивал, невозможно. При уходе сотрудника пароль остаётся у него.',
          },
        ],
      },
      {
        id: 'option-workspace',
        title: 'Рабочее пространство с доступом к системам команды',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'Третий вариант устроен иначе. Компания подключает не отдельных людей к модели, а модель к рабочим системам: трекеру задач, базе знаний, репозиторию, почте и календарю. Сотрудник перестаёт пересказывать контекст, потому что агент читает его сам.',
          },
          {
            type: 'paragraph',
            text: 'Это требует больше подготовки на старте: нужно решить, к каким источникам открывать доступ, с какими правами и какие действия разрешать без участия человека. Взамен появляется то, чего нет в первых двух вариантах: повторяемость, общий счёт и возможность проверить, откуда взялся ответ.',
          },
        ],
      },
      {
        id: 'what-to-check',
        title: 'Что проверить перед выбором',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Инструменты для команд отличаются сильнее, чем кажется по описаниям на сайтах. Полезно пройтись по короткому списку вопросов и получить на каждый конкретный ответ.',
          },
          {
            type: 'list',
            items: [
              'К каким системам подключается ИИ и есть ли среди них ваш трекер задач, база знаний и репозиторий.',
              'Видно ли, откуда взят ответ: указана ли конкретная задача, страница или письмо.',
              'Наследует ли агент права сотрудника или получает доступ ко всем данным сразу.',
              'Требуется ли подтверждение человека перед действиями, которые что-то меняют во внешних системах.',
              'Можно ли сохранить удачный сценарий и переиспользовать его, а не собирать заново каждый раз.',
              'Как оформляется оплата: счёт на юридическое лицо или сбор чеков с личных карт сотрудников.',
            ],
          },
          {
            type: 'callout',
            title: 'Начинайте с одного процесса',
            body: 'Одновременная раздача доступа всей компании обычно заканчивается всплеском интереса и тишиной через месяц. Один регулярный процесс и небольшая группа дают проверяемый результат, на который можно опереться при расширении.',
          },
        ],
      },
      {
        id: 'how-laplace-works',
        title: 'Как это устроено в Laplace',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Laplace — это рабочее пространство команды, в котором агент подключается к системам, где лежит работа, и отвечает уже на их основе. Вместо личного чата у каждого появляется общая среда с настроенными ролями и правилами.',
          },
          {
            type: 'list',
            items: [
              'Подключаются Jira, Confluence, GitHub, рабочая почта и календарь.',
              'Ответ сопровождается ссылками на источники, поэтому его можно проверить.',
              'Агент работает в границах прав подключённых учётных записей и не видит больше, чем разрешено.',
              'Действия, которые что-то меняют во внешних системах, выполняются после подтверждения человека.',
              'Настроенные сценарии сохраняются в рабочем пространстве и остаются в компании, а не у отдельного сотрудника.',
            ],
          },
          {
            type: 'paragraph',
            text: 'Тариф Free рассчитан на четырнадцать дней и одного пользователя: этого достаточно, чтобы проверить один процесс. Тариф Team стоит 2 300 рублей за пользователя в месяц или 1 990 рублей при оплате за год и рассчитан на команду до двадцати человек. Для корпоративного масштаба и поставки в закрытом контуре есть Enterprise.',
          },
          {
            type: 'callout',
            title: 'Что остаётся за человеком',
            body: 'Агент собирает данные, сопоставляет источники и готовит результат. Значимые решения и подтверждение изменений остаются за сотрудником. Это осознанное ограничение, а не временное состояние продукта.',
          },
        ],
      },
      {
        id: 'how-to-start',
        title: 'С чего начать',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Переход от личных подписок к общему доступу не требует большого проекта. Достаточно выбрать один процесс и довести его до измеримого результата.',
          },
          {
            type: 'list',
            items: [
              'Выберите повторяющуюся задачу, которая требует данных из нескольких систем: подготовку сводки по проекту, разбор входящих обращений или сборку описания релиза.',
              'Определите, какие источники нужны для неё и кто отвечает за доступ к каждому.',
              'Подключите только эти источники и соберите сценарий для одной небольшой группы.',
              'Сравните результат с тем, как задача выполнялась раньше: по времени и по числу возвратов на доработку.',
              'Расширяйте доступ только на те процессы, где разница подтвердилась.',
            ],
          },
        ],
      },
      {
        id: 'faq',
        title: 'Частые вопросы',
        level: 2,
        blocks: [
          {
            type: 'faq',
            items: [
              {
                question:
                  'Чем корпоративный доступ к ИИ отличается от личных подписок сотрудников?',
                answer:
                  'Личная подписка даёт доступ к модели, и весь рабочий контекст сотрудник переносит вручную. Корпоративный доступ подключает ИИ к системам компании, поэтому агент читает задачи и документы сам, а настройки и результаты остаются в общем пространстве.',
              },
              {
                question: 'Можно ли просто купить один аккаунт на всю команду?',
                answer:
                  'Не стоит. Совместное использование одной учётной записи нарушает условия большинства сервисов и может привести к блокировке вместе с историей. Кроме того, теряется авторство запросов, а при уходе сотрудника доступ остаётся у него.',
              },
              {
                question: 'Сколько стоит дать команде доступ к ИИ?',
                answer:
                  'В Laplace тариф Free рассчитан на четырнадцать дней и одного пользователя, тариф Team стоит 2 300 рублей за пользователя в месяц или 1 990 рублей при оплате за год и рассчитан на команду до двадцати человек. Условия для корпоративного масштаба обсуждаются отдельно.',
              },
              {
                question: 'Нужно ли обучать модель на данных компании?',
                answer:
                  'Нет. Агент обращается к подключённым системам в момент запроса и берёт из них актуальные данные. Отдельное обучение на корпоративных данных для такого сценария не требуется.',
              },
              {
                question: 'Где обрабатываются данные компании?',
                answer:
                  'Состав подключённых источников, права доступа и контур поставки фиксируются до запуска. Laplace доступен в облачном варианте, а для закрытого корпоративного контура архитектура согласуется отдельно в рамках Enterprise.',
              },
              {
                question: 'С какой команды имеет смысл начинать?',
                answer:
                  'С той, где есть регулярный процесс с проверяемым результатом и данные лежат в нескольких системах сразу. Обычно это разработка, поддержка или проектное управление.',
              },
            ],
          },
        ],
      },
    ],
  },
]
